import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Linkedin, MapPin, Send, CalendarDays, Loader2 } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

export const Contact = ({ onBookClick }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    if (!form.message.trim()) err.message = "Message is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent — Deepshikha will respond within 1 business day.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again or email directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#051024]" data-testid="contact-section">
      <div className="absolute inset-0 bg-grid-gold opacity-20 pointer-events-none" />
      <div className="relative px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: contact info */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-5"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Contact</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight text-balance" data-testid="contact-heading">
            Let's discuss your <em className="text-[#D4AF37] font-medium">next program.</em>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 text-[#A8B2D1] text-lg leading-relaxed">
            Whether you're standing up a PMO, untangling RFQ-to-order, or rolling out an ERP — start with a focused
            conversation.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 space-y-5">
            <a
              href="https://www.linkedin.com/in/shikhajaiswal-pmp/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 group"
              data-testid="contact-linkedin"
            >
              <div className="w-11 h-11 border border-[#233554] group-hover:border-[#D4AF37] flex items-center justify-center transition-colors">
                <Linkedin size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#A8B2D1]">LinkedIn</p>
                <p className="text-[#F8F9FA] group-hover:text-[#D4AF37] transition-colors font-serif">
                  /in/shikhajaiswal-pmp
                </p>
              </div>
            </a>
            <a
              href="mailto:Shikha.JaiswalPMP@outlook.com"
              className="flex items-start gap-4 group"
              data-testid="contact-email"
            >
              <div className="w-11 h-11 border border-[#233554] group-hover:border-[#D4AF37] flex items-center justify-center transition-colors">
                <Mail size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#A8B2D1]">Email</p>
                <p className="text-[#F8F9FA] group-hover:text-[#D4AF37] transition-colors font-serif break-all">
                  Shikha.JaiswalPMP@outlook.com
                </p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 border border-[#233554] flex items-center justify-center">
                <MapPin size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#A8B2D1]">Engagement Footprint</p>
                <p className="text-[#F8F9FA] font-serif">Global · Remote-first · On-site available</p>
              </div>
            </div>
          </motion.div>

          <motion.button
            variants={fadeUp}
            onClick={onBookClick}
            className="mt-10 inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors px-7 py-4 text-sm font-semibold tracking-wide"
            data-testid="contact-book-btn"
          >
            <CalendarDays size={16} />
            Schedule a Time Instead
          </motion.button>
        </motion.div>

        {/* Right: form */}
        <motion.form
          onSubmit={onSubmit}
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-7 bg-[#0A192F] border border-[#233554] p-8 md:p-12"
          data-testid="contact-form"
        >
          <motion.h3 variants={fadeUp} className="font-serif text-2xl text-[#F8F9FA] mb-2">
            Send a message
          </motion.h3>
          <motion.p variants={fadeUp} className="text-sm text-[#A8B2D1] mb-8">
            Typical response time: 1 business day.
          </motion.p>

          <motion.div variants={fadeUp} className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
            <div>
              <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none transition-colors w-full"
                placeholder="Your name"
                data-testid="contact-input-name"
              />
              {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none transition-colors w-full"
                placeholder="you@company.com"
                data-testid="contact-input-email"
              />
              {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Company</label>
              <input
                type="text"
                value={form.company}
                onChange={update("company")}
                className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none transition-colors w-full"
                placeholder="Organization name"
                data-testid="contact-input-company"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-[#A8B2D1] uppercase tracking-widest mb-2 block">Message *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="bg-transparent border-b border-[#233554] focus:border-[#D4AF37] text-white py-2 outline-none transition-colors w-full resize-none"
                placeholder="Tell us about your project, timeline, and challenges..."
                data-testid="contact-input-message"
              />
              {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message}</p>}
            </div>
          </motion.div>

          <motion.button
            variants={fadeUp}
            type="submit"
            disabled={loading}
            className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] text-[#051024] hover:bg-[#F1D570] transition-colors px-8 py-4 text-sm font-semibold tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="contact-submit-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending..." : "Send Message"}
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};
